import React, { useState, useEffect, useRef } from "react"
import { initializeApp } from "firebase/app"

import { getFirestore, collection, query, where } from "firebase/firestore"
import { useCollectionData } from "react-firebase-hooks/firestore"
import { useMediaQuery } from "react-responsive"
import { Helpers } from "@flexday/common-react"
import { INITIAL_GEOLOCATION } from "../utils/constants"
import { getDistanceFromLatLonInKm } from "../utils/helpers"
import { tabOptions } from "./components/tabs/tab-data"
import CustomGoogleMap from "./components/google-map"
import CustomGooglePlacesAutoComplete from "./components/google-places-auto-complete"
import InventoryCard from "../reusable-components/inventory-card"
import "./map.css"

const Map = ({ defaultLocation }) => {
  const { getFirebaseConfig } = Helpers
  const firebaseConfig = getFirebaseConfig({
    REACT_APP_FIREBASE_API_KEY: process.env.REACT_APP_FIREBASE_API_KEY,
    REACT_APP_FIREBASE_APP_ID: process.env.REACT_APP_FIREBASE_APP_ID,
    REACT_APP_FIREBASE_MESSAGE_SENDER_ID:
      process.env.REACT_APP_FIREBASE_MESSAGE_SENDER_ID,
    REACT_APP_FIREBASE_PROJECT_ID: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  })

  const app = initializeApp(firebaseConfig)
  const firestore = getFirestore(app)
  const meetingRoomLocationsRef = collection(firestore, "meetingRoomLocations")
  const privateOfficeLocationsRef = collection(
    firestore,
    "privateOfficeLocations"
  )

  const [meetingRoomLocations, loadingMeetingRoomLocations] = useCollectionData(
    query(meetingRoomLocationsRef, where("isVisible", "==", true))
  )
  const [privateOfficeLocations, loadingPrivateOfficeLocations] =
    useCollectionData(
      query(privateOfficeLocationsRef, where("isVisible", "==", true))
    )

  const [flexdaySuitesLocations, setFlexdaySuitesLocations] = useState([])
  const [filteredPrivateOfficeLocations, setFilteredPrivateOfficeLocations] =
    useState([])
  const [filteredMeetingRoomLocations, setFilteredMeetingRoomLocations] =
    useState([])

  const [selectedTab, setSelectedTab] = useState(tabOptions.MeetingRoom)
  const [geoLocation, setGeoLocation] = useState(
    defaultLocation || INITIAL_GEOLOCATION
  )
  const [poi, setPoi] = useState(null)
  const [currentUserLocation, setCurrentUserLocation] = useState(
    defaultLocation || INITIAL_GEOLOCATION
  )
  const [sortedLocations, setSortedLocations] = useState([])
  const [isPlaceSelected, setIsPlaceSelected] = useState(false)
  const [selectedMarker, setSelectedMarker] = useState(null)
  const [isPermissionGranted, setIsPermissionGranted] = useState(false)
  const inventoryCardsContainerRef = useRef(null)
  const isTabletOrDesktop = useMediaQuery({ query: "(min-width: 768px)" })

  const handleTabChange = (name) => {
    setSelectedTab(name)
  }

  const getLocations = () => {
    switch (selectedTab) {
      case tabOptions.FlexdaySuite:
        return flexdaySuitesLocations
      case tabOptions.MeetingRoom:
        return filteredMeetingRoomLocations
      case tabOptions.PrivateOffice:
        return filteredPrivateOfficeLocations
      default:
        return []
    }
  }

  const getSortedLocations = () => {
    const locations = getLocations()
    const locationsWithDistance = locations?.map((location) => {
      location.distance = getDistanceFromLatLonInKm(
        geoLocation.lat,
        geoLocation.lng,
        location.latitude,
        location.longitude
      )
      return location
    })
    return locationsWithDistance?.sort((a, b) => a.distance - b.distance)
  }

  const scrollToCard = (cardRef) => {
    if (isTabletOrDesktop) {
      const relativeTop =
        window.scrollY > inventoryCardsContainerRef.current.offsetTop
          ? window.scrollY
          : inventoryCardsContainerRef.current.offsetTop
      inventoryCardsContainerRef.current.scrollTo({
        behavior: "smooth",
        top: cardRef.offsetTop - relativeTop,
      })
    }
  }

  const handleMarkerClick = (markerId) => {
    setSelectedMarker(markerId)
    const cardRef = document.getElementById(markerId)
    scrollToCard(cardRef)
  }

  const handleMarkerReset = () => {
    setSelectedMarker(null)
    setSortedLocations(getSortedLocations())
  }

  const onCardSelected = (id) => {
    handleMarkerClick(id)
    const cardRef = document.getElementById(id)
    scrollToCard(cardRef)
  }

  useEffect(() => {
    if (!loadingMeetingRoomLocations && !loadingPrivateOfficeLocations) {
      setFlexdaySuitesLocations(
        privateOfficeLocations.filter((location) =>
          location.name.startsWith("Flexday Suites")
        )
      )
      setFilteredPrivateOfficeLocations(
        privateOfficeLocations.filter(
          (location) => !location.name.startsWith("Flexday Suites")
        )
      )
      setFilteredMeetingRoomLocations(
        meetingRoomLocations.filter(
          (location) => !location.name.startsWith("Flexday Suites")
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMeetingRoomLocations, loadingPrivateOfficeLocations])

  useEffect(() => {
    handleMarkerReset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab])

  const handlePlaceSelected = (place) => {
    setGeoLocation({
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    })
    setPoi({
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    })
    setIsPlaceSelected(true)
  }

  const handleMapDragged = (newGeoLocation) => {
    setGeoLocation(newGeoLocation)
  }

  const permissionGranted = (newGeoLocation) => {
    setGeoLocation({
      lat: newGeoLocation.coords.latitude,
      lng: newGeoLocation.coords.longitude,
    })
    setCurrentUserLocation({
      lat: newGeoLocation.coords.latitude,
      lng: newGeoLocation.coords.longitude,
    })
    setPoi({
      lat: newGeoLocation.coords.latitude,
      lng: newGeoLocation.coords.longitude,
    })
    setIsPermissionGranted(true)
    setIsPlaceSelected(true)
  }

  const getLocationPermission = () => {
    if (navigator.geolocation) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then(function (result) {
          if (result.state === "granted") {
            navigator.geolocation.getCurrentPosition(
              permissionGranted,
              errors,
              options
            )
          } else if (result.state === "prompt") {
            navigator.geolocation.getCurrentPosition(
              permissionGranted,
              errors,
              options
            )
          } else if (result.state === "denied") {
            setGeoLocation(currentUserLocation)
            setPoi(currentUserLocation)
          }
        })
    }
  }

  const handleResetMapCenter = () => {
    if (isPermissionGranted) {
      setGeoLocation(currentUserLocation)
    } else {
      getLocationPermission()
    }
    setIsPlaceSelected(true)
  }

  const handleClearPlace = () => {
    setGeoLocation(currentUserLocation)
    setIsPlaceSelected(false)
    setSelectedMarker(null)
  }

  const options = {
    enableHighAccuracy: true,
    timeout: 15000,
    maximumAge: 0,
  }

  const errors = (err) => {
    console.warn(`ERROR(${err.code}): ${err.message}`)
  }

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then(function (result) {
          if (result.state === "granted" && !defaultLocation) {
            navigator.geolocation.getCurrentPosition(
              permissionGranted,
              errors,
              options
            )
          }
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!loadingMeetingRoomLocations && !loadingPrivateOfficeLocations) {
      setSortedLocations(getSortedLocations())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loadingMeetingRoomLocations,
    loadingPrivateOfficeLocations,
    geoLocation,
    filteredMeetingRoomLocations,
    filteredPrivateOfficeLocations,
  ])

  useEffect(() => {
    if (sortedLocations && sortedLocations.length > 0) {
      setSelectedMarker(sortedLocations?.[0].id)
      const cardRef = document.getElementById(sortedLocations?.[0].id)
      scrollToCard(cardRef)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedLocations, isTabletOrDesktop])

  if (loadingMeetingRoomLocations || loadingPrivateOfficeLocations) {
    return <div>Loading...</div>
  }

  const renderInventoryCards = () => {
    return sortedLocations?.map((location) => {
      return (
        <InventoryCard
          key={location.id}
          location={location}
          selectedTab={selectedTab}
          selected={selectedMarker === location.id}
          onSelected={onCardSelected}
        />
      )
    })
  }

  const renderSelectedCard = () => {
    const selectedLocation = sortedLocations?.find(
      (location) => location.id === selectedMarker
    )
    if (selectedLocation) {
      return (
        <InventoryCard location={selectedLocation} selectedTab={selectedTab} />
      )
    }
  }

  return (
    <div className="flex flex-col gap-2 items-center justify-start pb-2 px-3 md:px-0">
      {!isTabletOrDesktop && (
        <CustomGooglePlacesAutoComplete
          handlePlaceSelected={handlePlaceSelected}
          handleClearPlace={handleClearPlace}
          geoLocation={geoLocation}
          className="w-full h-9 max-w-[570px]"
        />
      )}
      <div className="flex flex-col-reverse md:flex-row justify-start items-center md:justify-center md:items-start gap-1 md:gap-2 lg:gap-4 w-full px-0 md:px-3 lg:px-8 overflow-hidden">
        <div className="flex flex-col gap-3 justify-start items-center">
          {isTabletOrDesktop && (
            <CustomGooglePlacesAutoComplete
              handlePlaceSelected={handlePlaceSelected}
              handleClearPlace={handleClearPlace}
              geoLocation={geoLocation}
              className="md:w-72 lg:w-84"
            />
          )}
          {isTabletOrDesktop && (
            <div
              className="cards-container flex flex-col gap-4 justify-start items-center w-80 md:w-84 lg:w-97 max-h-[800px] px-6 overflow-y-auto overflow-x-hidden mb-6 ml-2"
              ref={inventoryCardsContainerRef}>
              {renderInventoryCards()}
            </div>
          )}
          {!isTabletOrDesktop && (
            <div className="w-72">{renderSelectedCard()}</div>
          )}
        </div>
        <CustomGoogleMap
          geoLocation={geoLocation}
          locations={sortedLocations}
          selectedTab={selectedTab}
          handleTabChange={handleTabChange}
          isPlaceSelected={isPlaceSelected}
          selectedMarker={selectedMarker}
          handleMarkerClick={handleMarkerClick}
          handleMapDragged={handleMapDragged}
          handleResetMapCenter={handleResetMapCenter}
          poi={poi}
        />
      </div>
    </div>
  )
}

export default Map
